/**
 * Pure helpers that render MDM distribution snippets from an
 * InstallerBuild. Backend doesn't ship these strings — we template
 * them from filename/sha256/downloadUrl/mdm.* fields.
 */
import type { InstallerBuild } from "@/types/edr-installers";

function fname(b: InstallerBuild): string {
  if (b.filename) return b.filename;
  const ext = b.platform === "macos" ? "pkg" : b.platform === "windows" ? "msi" : "deb";
  const safe = b.tagName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `smplify-edr-${safe}-${b.version}.${ext}`;
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function manualSnippet(b: InstallerBuild): string {
  const f = fname(b);
  if (b.platform === "macos") {
    return [
      `# Download`,
      `curl -L -o "${f}" "${b.downloadUrl}"`,
      ``,
      `# Verify integrity`,
      `echo "${b.sha256}  ${f}" | shasum -a 256 -c`,
      ``,
      `# Install (requires admin)`,
      `sudo installer -pkg "${f}" -target /`,
    ].join("\n");
  }
  if (b.platform === "windows") {
    return [
      `# Download (PowerShell, run as admin)`,
      `Invoke-WebRequest -Uri "${b.downloadUrl}" -OutFile "${f}"`,
      ``,
      `# Verify integrity`,
      `(Get-FileHash "${f}" -Algorithm SHA256).Hash -eq "${b.sha256.toUpperCase()}"`,
      ``,
      `# Install silently`,
      `msiexec /i "${f}" /qn /norestart`,
    ].join("\n");
  }
  return [
    `# Download`,
    `curl -L -o "${f}" "${b.downloadUrl}"`,
    ``,
    `# Verify integrity`,
    `echo "${b.sha256}  ${f}" | sha256sum -c`,
    ``,
    `# Install (Debian/Ubuntu)`,
    `sudo dpkg -i "${f}"`,
    `# or RHEL/Rocky/Alma:  sudo rpm -ivh "${f}"`,
  ].join("\n");
}

export function jamfSnippet(b: InstallerBuild): string {
  const f = fname(b);
  return [
    `# Jamf Pro — Install Smplify EDR (${b.tagName})`,
    `# 1. Download package to admin workstation`,
    `curl -L -o "${f}" "${b.downloadUrl}"`,
    `shasum -a 256 "${f}"   # expect: ${b.sha256}`,
    ``,
    `# 2. Jamf Pro Console`,
    `#    Settings → Computer Management → Packages → New`,
    `#    Upload: ${f}`,
    `#    Category: Security    Priority: 10`,
    ``,
    `# 3. Smart Computer Group`,
    `#    Name: "Smplify tag: ${b.tagName}"`,
    `#    Criterion mirroring the Smplify tag (LDAP group / asset tag).`,
    ``,
    `# 4. Policies → New`,
    `#    Name:      Install Smplify EDR — ${b.tagName}`,
    `#    Scope:     Smart Group above`,
    `#    Package:   ${f}  (Action: Install)`,
    `#    Trigger:   Recurring Check-in`,
    `#    Frequency: Once per computer`,
  ].join("\n");
}

export function kandjiSnippet(b: InstallerBuild): string {
  const f = fname(b);
  const pkgId = b.mdm?.pkgIdentifier || "com.smplify.edr.installer";
  return [
    `# Kandji — Custom App (${b.tagName})`,
    `# 1. Download installer`,
    `curl -L -o "${f}" "${b.downloadUrl}"`,
    ``,
    `# 2. Kandji Console`,
    `#    Library → Add New → Custom App`,
    `#    Upload:       ${f}`,
    `#    Install Type: Installer Package`,
    `#    Audit:        Audit and Enforce`,
    ``,
    `# 3. Enforcement`,
    `#    Install on existence of:  ${pkgId}`,
    ``,
    `# 4. Integrity (paste into SHA-256 field)`,
    `#    ${b.sha256}`,
    ``,
    `# 5. Assignment Map → Blueprint(s) for tag "${b.tagName}"`,
  ].join("\n");
}

export function mosyleSnippet(b: InstallerBuild): string {
  const f = fname(b);
  const bundleId = b.mdm?.bundleId || "com.smplify.edr";
  return [
    `# Mosyle — Custom App (${b.tagName})`,
    `# 1. Download installer`,
    `curl -L -o "${f}" "${b.downloadUrl}"`,
    ``,
    `# 2. Mosyle Console`,
    `#    Management → macOS → Custom Apps → Add new app`,
    `#    Upload:    ${f}`,
    `#    App name:  Smplify EDR — ${b.tagName}`,
    `#    Bundle ID: ${bundleId}`,
    ``,
    `# 3. Integrity check (SHA-256)`,
    `#    ${b.sha256}`,
    ``,
    `# 4. Assignment → Device Group mirroring tag "${b.tagName}"`,
  ].join("\n");
}

export function installerFilename(b: InstallerBuild): string {
  return fname(b);
}
